import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { AuthDescription } from '@/components/features/auth/auth-description';
import { SignupForm } from '@/components/features/auth/forms/signup-form';
import { LoginOrSignupWith } from '@/components/features/auth/login-or-signup-with';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PublicPage } from '@/components/ui/layout/public-page';
import { getPreviewOrganizationQuery } from '@/queries/getPreviewOrganizationQuery';

import { invitationSearchSchema } from '../invitation';

const emailSearchSchema = z.object({
  email: z.string().email().optional(),
});

const searchSchema = invitationSearchSchema.merge(emailSearchSchema);

export const Route = createFileRoute('/_public/signup')({
  component: Signup,
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: ({ context }) => {
    const isAuthenticated = context.isAuthenticated;

    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' });
    }
  },
});

function Signup() {
  const { t } = useTranslation();
  const search = Route.useSearch();

  const { data: previewOrganization } = useQuery(
    getPreviewOrganizationQuery({
      organizationId: search.organizationId,
      invitationCode: search.invitationCode,
    })
  );

  return (
    <PublicPage.Layout>
      <PublicPage.Header />
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            {t('auth.signup.title')}
          </CardTitle>
          <AuthDescription
            type="signup"
            organizationName={previewOrganization?.name}
          />
        </CardHeader>

        <CardContent>
          <LoginOrSignupWith
            type="signup"
            invitationFlow={{
              invitationCode: search.invitationCode,
              organizationId: search.organizationId,
            }}
          />

          <SignupForm
            email={search.email}
            invitationFlow={{
              invitationEmail: previewOrganization?.invitationEmail,
              organizationId: search.organizationId,
              invitationCode: search.invitationCode,
            }}
          />
        </CardContent>

        <CardFooter className="flex justify-center">
          <p className="text-sm text-muted-foreground">
            {t('auth.layout.footer.alreadyHaveAccount')}{' '}
            <Link
              to="/login"
              className="text-primary hover:underline"
              search={search}
            >
              {t('auth.layout.footer.login')}
            </Link>
          </p>
        </CardFooter>
      </Card>
      <PublicPage.Footer />
    </PublicPage.Layout>
  );
}
